import { AxiosResponse } from 'axios';
import { PaginationResponse } from '../models/pagination-response';
import { DeviceLinking, LinkingDeviceRequest, MerchantDetailModel, Terminal, TerminalLinking } from '../models/terminal';
import { deleteAsync, getAsync, postAsync, putAsync } from './http-client';
import { MerchantInfo } from '@/models/merchant';
import { DefaultDeviceRequest, Device } from '@/models/device';
import { DeviceFilterType } from '@/models/enums';
import { LocationDetailModel, LocationResponseModel } from '@/models/location';

const baseUrl = `${process.env.REACT_APP_API_ENDPOINT_Portal}/v1`;


function getDeviceLinking(
  pageIndex: number,
  pageSize: number,
  searchString: string,
  deviceId: string,
): Promise<AxiosResponse<PaginationResponse<DeviceLinking>>> {
  const url = `${baseUrl}/devicemanagement/available-linking-devices/${deviceId}?pageIndex=${pageIndex}&pageSize=${pageSize}&searchString=${searchString}`;
  return getAsync(url);
}

function linkingDevice(
  model: LinkingDeviceRequest
): Promise<AxiosResponse<LinkingDeviceRequest>> {
  const url = `${baseUrl}/devicemanagement/link-device`;
  return postAsync(url, model);
}

function getKiosks(
  pageIndex: number,
  pageSize: number,
  searchString: string
): Promise<AxiosResponse<PaginationResponse<Terminal>>> {
  const url = `${baseUrl}/devicelinking/kiosks?pageIndex=${pageIndex}&pageSize=${pageSize}&searchString=${searchString}`;
  return getAsync(url);
}

function getPaymentsByKiosk(
  kioskId: string,
  pageIndex: number,
  pageSize: number,
  searchString: string
): Promise<AxiosResponse<PaginationResponse<TerminalLinking>>> {
  const url = `${baseUrl}/devicelinking/payments?kioskId=${kioskId}&pageIndex=${pageIndex}&pageSize=${pageSize}&searchString=${searchString}`;
  return getAsync(url);
}

function getKiosksByPayment(
  paymentId: string,
  pageIndex: number,
  pageSize: number,
  searchString: string
): Promise<AxiosResponse<PaginationResponse<TerminalLinking>>> {
  const url = `${baseUrl}/devicelinking/kiosk-payments?paymentId=${paymentId}&pageIndex=${pageIndex}&pageSize=${pageSize}&searchString=${searchString}`;
  return getAsync(url);
}

function getLocations(): Promise<AxiosResponse<LocationResponseModel[]>> {
  const url = `${baseUrl}/devicelinking/locations`;
  return getAsync(url);
}

function getLocation(id: string): Promise<AxiosResponse<LocationDetailModel>> {
  const url = `${baseUrl}/location?locationId=${id}`;
  return getAsync(url);
}

function getTerminalListByKey(
  locationKey: string,
  type: DeviceFilterType,
  pageIndex: number,
  pageSize: number,
  searchString: string
): Promise<AxiosResponse<PaginationResponse<TerminalLinking>>> {
  const url = `${baseUrl}/devicelinking/location-devices?locationKey=${locationKey}&type=${type}&pageIndex=${pageIndex}&pageSize=${pageSize}&searchString=${searchString}`;
  return getAsync(url);
}

function getTerminalDetail(id: string): Promise<AxiosResponse<Terminal>> {
  const url =   `${baseUrl}/devicelinking/terminal/${id}`;
  return getAsync(url);
}

function postMerchant(
  model: MerchantInfo
): Promise<AxiosResponse<MerchantInfo>> {
  const url = `${baseUrl}/merchant`;
  return postAsync(url, model);
}

function updateMerchant(
  model: MerchantInfo
): Promise<AxiosResponse<MerchantInfo>> {
  const url = `${baseUrl}/merchant`;
  return putAsync(url, model);
}

function addDevice(model: Device, locationId?: string, merchantId?: string) : Promise<AxiosResponse> {
  const url = `${baseUrl}/devicemanagement/add/${locationId}/${merchantId}`;
  return postAsync(url, model);
}

function updateDevice(model: Device, deviceId?: string) : Promise<AxiosResponse> {
  const url = `${baseUrl}/devicemanagement/update/${deviceId}`;
  return postAsync(url, model);
}

function setDefaultDevice(model: DefaultDeviceRequest) : Promise<AxiosResponse> {
  const url = `${baseUrl}/devicemanagement/set-default`;
  return postAsync(url, model);
}

function activateDevice(deviceId?: string, isActive?: boolean) : Promise<AxiosResponse> {
  const url = `${baseUrl}/devicemanagement/active/${deviceId}/${isActive}`;
  return postAsync(url);
}

function removeDevice(deviceId?: string) : Promise<AxiosResponse> {
  const url = `${baseUrl}/devicemanagement/${deviceId}`;
  return deleteAsync(url);
}

export const TerminalApi = {
  getKiosks,
  getPaymentsByKiosk,
  getLocations,
  getLocation,
  getTerminalListByKey,
  getTerminalDetail,
  getKiosksByPayment,
  postMerchant,
  updateMerchant,
  addDevice,
  updateDevice,
  getDeviceLinking,
  linkingDevice,
  activateDevice,
  removeDevice,
  setDefaultDevice
};
